import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Bell, ChevronRight, Menu, Search, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Text } from '@/components/flup';
import { NAV } from './nav';

/**
 * A thin top bar: where you are on the left, global affordances on the right.
 *
 * Page titles live in each page's own `PageHeader`, not here — repeating the
 * title in both is the fastest way to make a dashboard feel padded. The header
 * carries only the breadcrumb, search, notifications, and the assistant entry
 * point that is coming.
 */

interface HeaderProps {
  onOpenMobileNav: () => void;
  /** Opens the assistant. Wired up when the AI work lands. */
  onOpenAssistant?: () => void;
  notificationCount?: number;
}

/** Derive the trail from the nav, so a renamed nav item renames the crumb too. */
const useCrumbs = () => {
  const { pathname } = useLocation();

  return React.useMemo(() => {
    const match = NAV.flatMap((group) =>
      group.items.map((item) => ({ group: group.title, item })),
    )
      .filter(({ item }) => pathname === item.to || pathname.startsWith(`${item.to}/`))
      // The longest matching path is the most specific one.
      .sort((a, b) => b.item.to.length - a.item.to.length)[0];

    if (!match) return [];

    const isChild = pathname !== match.item.to;
    return [match.group, match.item.label, ...(isChild ? ['Details'] : [])];
  }, [pathname]);
};

const Header: React.FC<HeaderProps> = ({
  onOpenMobileNav,
  onOpenAssistant,
  notificationCount = 0,
}) => {
  const crumbs = useCrumbs();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 h-16 shrink-0 bg-flup-surface/85 backdrop-blur border-b border-flup-hairline">
      <div className="flex items-center justify-between gap-4 h-full px-4 sm:px-6">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onOpenMobileNav}
            aria-label="Open menu"
            className="lg:hidden grid place-items-center w-9 h-9 rounded-flup-sm text-flup-ink-2 hover:bg-flup-surface-alt transition-colors"
          >
            <Menu className="w-5 h-5" />
          </button>

          <nav aria-label="Breadcrumb" className="hidden sm:flex items-center gap-1.5 min-w-0">
            {crumbs.map((crumb, i) => (
              <React.Fragment key={`${crumb}-${i}`}>
                {i > 0 ? (
                  <ChevronRight className="w-3.5 h-3.5 text-flup-ink-3 shrink-0" aria-hidden />
                ) : null}
                <Text
                  variant="caption"
                  tone={i === crumbs.length - 1 ? 'primary' : 'tertiary'}
                  as="span"
                  lines={1}
                >
                  {crumb}
                </Text>
              </React.Fragment>
            ))}
          </nav>
        </div>

        <div className="flex items-center gap-1.5 shrink-0">
          <button
            type="button"
            onClick={() => navigate('/dashboard/bookings')}
            aria-label="Search bookings"
            className="grid place-items-center w-9 h-9 rounded-flup-sm text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
          >
            <Search className="w-[18px] h-[18px]" aria-hidden />
          </button>

          <button
            type="button"
            aria-label={`Notifications${notificationCount ? `, ${notificationCount} unread` : ''}`}
            className="relative grid place-items-center w-9 h-9 rounded-flup-sm text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors"
          >
            <Bell className="w-[18px] h-[18px]" aria-hidden />
            {notificationCount > 0 ? (
              <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-pill bg-flup-danger ring-2 ring-flup-surface" />
            ) : null}
          </button>

          {/* The assistant's home. Disabled until the AI work lands, but present
              so the header's balance is designed around it from the start. */}
          <button
            type="button"
            onClick={onOpenAssistant}
            disabled={!onOpenAssistant}
            title={onOpenAssistant ? 'Ask the assistant' : 'Assistant — coming soon'}
            className={cn(
              'inline-flex items-center gap-2 h-9 px-3 rounded-flup-sm transition-colors',
              onOpenAssistant
                ? 'bg-flup-brand-soft text-flup-brand-ink hover:bg-[#D8F2EA]'
                : 'bg-flup-surface-alt text-flup-ink-3 cursor-default',
            )}
          >
            <Sparkles className="w-4 h-4 shrink-0" aria-hidden />
            <Text variant="labelSemi" tone="inherit" as="span" className="hidden sm:inline">
              Ask AI
            </Text>
          </button>
        </div>
      </div>
    </header>
  );
};

export default Header;
